import { forwardRef } from 'react'
import type { InputHTMLAttributes, ReactNode } from 'react'
import { ErrorMessage } from './FormField'

interface RadioOption {
  value: string
  label: string
}

interface RadioGroupProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'required' | 'onChange'> {
  label: ReactNode
  options: RadioOption[]
  error?: string
  helperText?: string
  required?: boolean
  name: string
  value?: string
  onChange?: (value: string) => void
}

const RadioGroup = forwardRef<HTMLInputElement, RadioGroupProps>(
  ({ label, options, error, helperText, className = '', required, name, value, onChange, ...props }, ref) => {
    return (
      <div className="flex flex-col gap-3">
        <label className="text-base font-light text-neutral-900 tracking-wide">
          {label}
          {required && <span className="text-red-600 ml-1">*</span>}
        </label>

        <div className="flex flex-wrap gap-3">
          {options.map((option, index) => {
            const isSelected = value === option.value
            return (
              <label
                key={option.value}
                className={`
                  relative flex items-center justify-center
                  px-6 py-3 rounded-lg border-2
                  cursor-pointer transition-all duration-200
                  select-none font-medium text-sm
                  outline-none
                  ${isSelected
                    ? error
                      ? 'bg-red-600 border-red-600 text-white shadow-sm'
                      : 'bg-primary border-primary text-white shadow-sm'
                    : error
                      ? 'bg-white border-red-600 text-red-700 hover:bg-red-50'
                      : 'bg-white border-neutral-300 text-neutral-700 hover:border-neutral-400 hover:bg-neutral-50'}
                  ${className}
                `}
              >
                <input
                  ref={index === 0 ? ref : undefined}
                  type="radio"
                  name={name}
                  value={option.value}
                  checked={isSelected}
                  onChange={(e) => onChange?.(e.target.value)}
                  className="absolute opacity-0 w-0 h-0 focus-visible:outline-none peer"
                  aria-required={required}
                  {...props}
                />
                <span className="relative z-10">{option.label}</span>
                <div className={`
                  absolute inset-0 rounded-lg
                  peer-focus-visible:ring-2 peer-focus-visible:ring-offset-2
                  ${error ? 'peer-focus-visible:ring-red-500' : 'peer-focus-visible:ring-blue-500'}
                `} />
              </label>
            )
          })}
        </div>

        {helperText && !error && (
          <p className="text-sm text-neutral-500 font-light">{helperText}</p>
        )}
        {error && <ErrorMessage error={error} />}
      </div>
    )
  }
)

RadioGroup.displayName = 'RadioGroup'

export default RadioGroup
